import { AfterViewInit, Component, ElementRef, EventEmitter, HostBinding, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { NgControl } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteSelectedEvent, MatFormFieldControl, MatInput } from '@angular/material';
import { fromEvent, Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { AddressSearchApiService, AddressSearchData } from '../../../services/api/address-search-api.service';

export interface AddressAutocompletePickedAddress {
  street?: string;
  district?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  number?: string;
}

@Component({
  selector: 'mandae-address-autocomplete',
  templateUrl: './address-autocomplete.component.html',
  styleUrls: ['./address-autocomplete.component.scss'],
  providers: [
    { provide: MatFormFieldControl, useExisting: AddressAutocompleteComponent },
  ],
})
export class AddressAutocompleteComponent implements AfterViewInit, OnDestroy, MatFormFieldControl<AddressAutocompletePickedAddress> {

  private static nextId = 0;

  @ViewChild('addressAutocompleteInput')
  placesField: ElementRef<HTMLInputElement>;

  @ViewChild('addressAutocompleteInput', { read: MatInput })
  placesInput: MatInput;

  @ViewChild('addressAutocomplete')
  autocomplete: MatAutocomplete;

  @Input()
  public get placeholder() {
    return this._placeholder;
  }
  public set placeholder(value: string) {
    this._placeholder = value;
    this.stateChanges.next();
  }
  private _placeholder: string;

  @Output()
  public valueChange = new EventEmitter<AddressAutocompletePickedAddress>();

  private _value: AddressAutocompletePickedAddress = null;
  public get value() {
    return this._value;
  }
  public set value(value: AddressAutocompletePickedAddress) {
    this._value = value;
    this.stateChanges.next();
    this.valueChange.emit(value);
  }

  @HostBinding()
  public id = `mandae-address-autocomplete-${AddressAutocompleteComponent.nextId++}`;

  public ngControl: NgControl = null;

  public get focused() {
    if (!this.placesInput || !this.autocomplete) {
      return false;
    }

    return this.placesInput.focused || this.autocomplete.isOpen;
  }

  public get empty() {
    return !this.autocompleteInput;
  }

  @HostBinding('class.floating')
  public get shouldLabelFloat(): boolean {
    return this.focused || !this.empty;
  }

  @Input()
  public get required() {
    return this._required;
  }
  public set required(value: boolean) {
    this._required = value;
    this.stateChanges.next();
  }
  private _required = false;

  @Input()
  public get disabled() {
    return this._disabled;
  }
  public set disabled(value: boolean) {
    this._disabled = value;
    this.stateChanges.next();
  }
  private _disabled = false;

  public get errorState() {
    return false;
  }

  public get controlType() {
    return 'address-autocomplete';
  }

  @HostBinding('attr.aria-describedby')
  public describedBy = '';

  public stateChanges = new Subject<void>();

  public addressSource$: Observable<AddressSearchData[]> = null;

  public autocompleteInput = '';

  constructor(private api: AddressSearchApiService) { }

  ngAfterViewInit() {
    this.addressSource$ = fromEvent(this.placesField.nativeElement, 'keyup')
      .pipe(
        map(() => this.placesField.nativeElement.value),
        distinctUntilChanged(),
        debounceTime(50),
        switchMap(value => this.api.searchAddress(value).pipe(
          map(response => response.enderecosEncontrados),
          catchError(() => of([])),
        )),
      );
  }

  ngOnDestroy() {
    this.stateChanges.complete();
  }

  setDescribedByIds(ids: string[]) {
    this.describedBy = ids.join(' ');
  }

  onContainerClick(event: MouseEvent) {
    if (!this.focused && this.placesInput) {
      this.placesInput.focus();
    }
  }

  getAutocompleteAddressDisplay(value: AddressSearchData): string {
    if (!value) {
      return '';
    }

    return value.enderecoFormatado;
  }

  handleAddressChange($event: MatAutocompleteSelectedEvent) {
    const data = $event.option.value;

    this.value = {
      street: data.logradouro,
      city: data.cidade,
      district: data.bairro,
      postalCode: data.cep,
      state: data.estado,
      number: data.numero,
    };
  }

}
